import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import { Card } from '@els/els-react--card';
// @ts-ignore
import { Icon } from '@els/els-react--icon';
// @ts-ignore
import { Loader } from '@els/els-react--loader';
// @ts-ignore
import { Flyout } from '@els/els-react--flyout';
// @ts-ignore
import { LinkElement } from '@els/els-react--link-element';

import {
    getPendingAssignment,
    getAllUserAssignmentCoursesById,
} from './my-learning-card-thunk';
import {
    selectUserAssignmentCourses,
    selectUserAssignmentCoursesAsyncStatus,
    selectPendingAssignmentAsyncStatus,
    selectAssignmentDetails,
} from './my-learning-card-selector';
import {
    hasSucceeded,
    isInitial,
    isPending,
} from '../../../../../libs/utils/constants/async-status';
import {
    selectAppConfigs,
    selectAppRuntimeConfigsAsyncStatus,
} from '../../../app-config/app-config-selector';

import { ICourseData } from '../../../../../libs/utils/interfaces/my-learning-interface';
import { formatDate } from '../../../../../libs/utils/helper/date-helper';
// @ts-ignore
import NoAssignmentsDue from '../../../../assets/img/no-assignments-due.svg';
// @ts-ignore
import NoAssignmentsAssigned from '../../../../assets/img/no-assignments-assigned.svg';
import { MyLearningCardType } from '../../../../constants/my-learning-card-type';

import ProgressBar from '../../../../components/progress-bar/progress-bar';
import './my-learning-card.scss';
import LearningCardLogoutView from './my-learning-card-logout-view';

const MyLearningCard = (props: any) => {
    const { clientId, userId, isUserloggedIn: isUserLoggedIn } = props;
    const dispatch = useDispatch();
    const baseClassName = 'my-learning-card';
    const pendingAssignmentAsyncStatus = useSelector(
        selectPendingAssignmentAsyncStatus
    );
    const appConfigs = useSelector(selectAppConfigs);
    const appRuntimeConfigsAsyncStatus = useSelector(
        selectAppRuntimeConfigsAsyncStatus
    );
    const { CONNECT_ADAPTER_BASE_URL } = appConfigs;
    const trustedOrigin = CONNECT_ADAPTER_BASE_URL
        ? new URL(CONNECT_ADAPTER_BASE_URL).origin
        : '';
    const userAssignmentCoursesAsyncStatus = useSelector(
        selectUserAssignmentCoursesAsyncStatus
    );
    const userAssignmentCourses = useSelector(selectUserAssignmentCourses);
    const { courseDetails, assignmentId } = userAssignmentCourses;
    const { assignment, type }: any = useSelector(selectAssignmentDetails);

    useEffect(() => {
        if (isUserLoggedIn) {
            dispatch(getPendingAssignment({}));
        }
    }, [dispatch, clientId, userId]);

    useEffect(() => {
        if (hasSucceeded(pendingAssignmentAsyncStatus) && assignment) {
            dispatch(
                getAllUserAssignmentCoursesById({
                    userId,
                    clientId,
                    userAssignmentId: assignment.id,
                    isAutoAssigned: assignment.isAutoAssigned,
                })
            );
        }
    }, [clientId, userId, dispatch, assignment, pendingAssignmentAsyncStatus]);

    const redirectToContinue = (courseDetails: any[]) => {
        const launchUrl = `${
            appConfigs.CONNECT_ADAPTER_BASE_URL
        }/launch?lessonId=${
            courseDetails[0].id
        }&assignmentId=${assignmentId}${isCustomOrSkillsContent(
            courseDetails,
            courseDetails[0].id
        )}`;
        window.open(
            launchUrl,
            'connect adapter link',
            'width=1200,height=1000'
        );
    };

    const redirectToSurvey = () => {
        window.location.href = `${appConfigs.CE_BASE_URL}/license-demographic`;
    };
    const redirectToTranscript = () => {
        window.location.href = `${appConfigs.CE_BASE_URL}/transcript`;
    };

    const redirectToMyLearning = () => {
        window.location.href = `${appConfigs.NEURON_BASE_URL}/my-learning`;
    };

    window.onmessage = (e: any) => {
        if (!e || e.origin !== trustedOrigin || !e.data) {
            return;
        }
        if (e.data.eventName === 'exit-course-launch') {
            if (e.data.assignmentId) {
                if (isUserLoggedIn) {
                    dispatch(
                        getPendingAssignment({
                            assignmentId: e.data.assignmentId,
                        })
                    );
                }
            }
        }
    };

    const buttonClickRedirect = (buttonLabel: string, courseDetails: any[]) => {
        if (buttonLabel === 'Start') {
            redirectToContinue(courseDetails);
        } else if (buttonLabel === 'Continue') {
            redirectToContinue(courseDetails);
        } else if (buttonLabel === 'Take survey') {
            redirectToSurvey();
        }
    };

    const getLeftOffLesson = (
        courseDetails: any[],
        assignmentStartDate: string | null
    ) => {
        if (!courseDetails || courseDetails.length === 0) return null;

        const surveyLesson = courseDetails.find(
            (lesson) =>
                lesson.startDate &&
                lesson.testCompletionDate &&
                lesson.isSurveyPending
        );
        if (surveyLesson) {
            return { buttonLabel: 'Take survey', lesson: surveyLesson };
        }

        const continueLesson = courseDetails.find(
            (lesson) =>
                (assignmentStartDate && lesson.startDate === null) ||
                (assignmentStartDate &&
                    lesson.startDate !== null &&
                    !lesson.testCompletionDate)
        );
        if (continueLesson) {
            return { buttonLabel: 'Continue', lesson: continueLesson };
        }

        return {
            buttonLabel: 'Start',
            lesson: courseDetails[0],
        };
    };

    const leftOffLesson =
        hasSucceeded(pendingAssignmentAsyncStatus) && assignment
            ? getLeftOffLesson(courseDetails, assignment.startDate)
            : null;
    const buttonLabel = leftOffLesson ? leftOffLesson.buttonLabel : 'Start';

    const getButtonHref = () => {
        if (buttonLabel === 'Take survey') {
            return `${appConfigs.CE_BASE_URL}/license-demographic`;
        }
        if (courseDetails && courseDetails.length > 0) {
            return `${appConfigs.CONNECT_ADAPTER_BASE_URL}/launch?lessonId=${
                courseDetails[0].id
            }&assignmentId=${assignmentId}${isCustomOrSkillsContent(
                courseDetails,
                courseDetails[0].id
            )}`;
        }
        return '#';
    };

    const getButtonAriaLabel = () => {
        if (buttonLabel === 'Start') return 'Start learning';
        if (buttonLabel === 'Continue') return 'Continue learning';
        if (buttonLabel === 'Take survey') return 'Take survey';
        return buttonLabel;
    };

    const isCustomOrSkillsContent = (
        courseDetails: any,
        lessonId: number | null
    ) => {
        const filteredDetails = courseDetails.filter(
            (course: ICourseData) => course.id === lessonId
        );
        let getTypeOfContent: string | null = null;
        // eslint-disable-next-line no-nested-ternary
        getTypeOfContent = filteredDetails.some(
            (course: { type: string }) => course.type === 'Custom content'
        )
            ? '&contentType=CustomContent'
            : filteredDetails.some(
                  (course: { type: string }) => course.type === 'Skill'
              )
            ? '&contentType=SkillsContent'
            : '';
        return getTypeOfContent;
    };

    const renderAssignmentDetails = (cardType: string, assignment: any) => {
        const {
            assignmentDueDate,
            assignmentTitle,
            courseCount,
            completedCourseCount,
        } = assignment || {};
        const formattedDate = assignmentDueDate
            ? formatDate(assignmentDueDate, 'MMM dd, yyyy')
            : '';
        const truncatedAssignmentTitle =
            assignmentTitle && assignmentTitle.length > 40
                ? `${assignmentTitle.substring(0, 40)}...`
                : assignmentTitle;
        const isLoading =
            isPending(pendingAssignmentAsyncStatus) ||
            isPending(userAssignmentCoursesAsyncStatus);
        const isDataAvailable =
            hasSucceeded(pendingAssignmentAsyncStatus) &&
            (isInitial(userAssignmentCoursesAsyncStatus) ||
                hasSucceeded(userAssignmentCoursesAsyncStatus));
        return (
            <Card
                className={`${baseClassName}__content ${
                    cardType === MyLearningCardType.PAST_DUE_STATE &&
                    `${baseClassName}__content--past-due`
                }`}
                borderWeight={
                    cardType === MyLearningCardType.PAST_DUE_STATE
                        ? 'medium'
                        : 'light'
                }
                type={
                    cardType === MyLearningCardType.PAST_DUE_STATE
                        ? 'warn'
                        : undefined
                }
                size="medium"
            >
                <div className={`${baseClassName}__content_header`}>
                    {isDataAvailable &&
                        cardType === MyLearningCardType.PAST_DUE_STATE && (
                            <span>
                                <Icon
                                    a11y={{
                                        name: 'Information',
                                        description: 'Past due learning items',
                                    }}
                                    id="information-outline-square"
                                    className="information-outline-square"
                                    sprite="InformationOutlineSquare"
                                    size="m"
                                    color="negative"
                                />
                            </span>
                        )}
                    <LinkElement
                        href={`${appConfigs.NEURON_BASE_URL}/my-learning`}
                        className={`${baseClassName}__content_header-button-with-icon`}
                        iconSprite="ChevronRight"
                        iconRight
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            redirectToMyLearning();
                        }}
                    >
                        <h2>My Learning</h2>
                    </LinkElement>
                </div>
                {isLoading && (
                    <Loader
                        isVisible
                        isBlocking={false}
                        className={`${baseClassName}-loader`}
                    />
                )}
                {isDataAvailable &&
                    cardType === MyLearningCardType.PAST_DUE_STATE && (
                        <>
                            <div
                                className={`${baseClassName}__content_assignment-status`}
                            >
                                <span className="title-critical">PAST DUE</span>
                                <span className="date-critical">
                                    Due &nbsp;
                                    {formattedDate}
                                </span>
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-title`}
                            >
                                {assignmentTitle &&
                                assignmentTitle.length > 40 ? (
                                    <Flyout
                                        flyout={assignmentTitle}
                                        shouldCloseOnESC
                                        placement="bottom-start"
                                        trigger="hover"
                                    >
                                        {truncatedAssignmentTitle}
                                    </Flyout>
                                ) : (
                                    assignmentTitle
                                )}
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-progress_bar`}
                            >
                                <ProgressBar
                                    totalCount={courseCount}
                                    completedCount={completedCourseCount}
                                    earnableCE={
                                        userAssignmentCourses.earnableCE
                                    }
                                    showCountDetails
                                />
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-link_button_wrapper`}
                            >
                                <LinkElement
                                    href={getButtonHref()}
                                    iconSprite="ChevronRight"
                                    iconRight
                                    aria-label={getButtonAriaLabel()}
                                    className={`${baseClassName}__content_assignment-link_button_wrapper-button-with-icon`}
                                    onClick={
                                        (e: React.MouseEvent) => {
                                            e.preventDefault();
                                            buttonClickRedirect(
                                                buttonLabel,
                                                courseDetails
                                            );
                                        }
                                        // eslint-disable-next-line react/jsx-curly-newline
                                    }
                                >
                                    {buttonLabel}
                                </LinkElement>
                            </div>
                        </>
                    )}
                {isDataAvailable &&
                    cardType === MyLearningCardType.DUE_NEXT_STATE && (
                        <>
                            <div
                                className={`${baseClassName}__content_assignment-status`}
                            >
                                <span className="title">DUE NEXT</span>
                                <span className="due-next-date">
                                    Due &nbsp;
                                    {formattedDate}
                                </span>
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-title`}
                            >
                                {assignmentTitle &&
                                assignmentTitle.length > 40 ? (
                                    <Flyout
                                        flyout={assignmentTitle}
                                        shouldCloseOnESC
                                        placement="bottom-start"
                                        trigger="hover"
                                    >
                                        {truncatedAssignmentTitle}
                                    </Flyout>
                                ) : (
                                    assignmentTitle
                                )}
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-progress_bar`}
                            >
                                <ProgressBar
                                    totalCount={courseCount}
                                    completedCount={completedCourseCount}
                                    earnableCE={
                                        userAssignmentCourses.earnableCE
                                    }
                                    showCountDetails
                                />
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-link_button_wrapper`}
                            >
                                <LinkElement
                                    href={getButtonHref()}
                                    iconSprite="ChevronRight"
                                    iconRight
                                    aria-label={getButtonAriaLabel()}
                                    className={`${baseClassName}__content_assignment-link_button_wrapper-button-with-icon`}
                                    onClick={
                                        (e: React.MouseEvent) => {
                                            e.preventDefault();
                                            buttonClickRedirect(
                                                buttonLabel,
                                                courseDetails
                                            );
                                        }
                                        // eslint-disable-next-line react/jsx-curly-newline
                                    }
                                >
                                    {buttonLabel}
                                </LinkElement>
                            </div>
                        </>
                    )}
                {isDataAvailable &&
                    cardType === MyLearningCardType.NO_ASSIGNMENTS_DUE && (
                        <div
                            className={`${baseClassName}__content_no-assignments-due`}
                        >
                            <div
                                className={`${baseClassName}__content_no-assignments-due-body`}
                            >
                                <img
                                    src={NoAssignmentsDue}
                                    alt="No Assignments Due"
                                />
                                <div
                                    className={`${baseClassName}__content_no-assignments-due-body-text`}
                                >
                                    No assignments due —
                                    <br />
                                    well done!
                                </div>
                            </div>
                            <div
                                className={`${baseClassName}__content_no-assignments-due-descp`}
                            >
                                Take a moment to view your history and get your
                                certificates.
                            </div>
                            <div
                                className={`${baseClassName}__content_assignment-link_button_wrapper`}
                            >
                                <LinkElement
                                    href={`${appConfigs.CE_BASE_URL}/transcript`}
                                    iconSprite="ChevronRight"
                                    iconRight
                                    aria-label="Review my work"
                                    className={`${baseClassName}__content_assignment-link_button_wrapper-button-with-icon`}
                                    onClick={(e: React.MouseEvent) => {
                                        e.preventDefault();
                                        redirectToTranscript();
                                    }}
                                >
                                    Review my work
                                </LinkElement>
                            </div>
                        </div>
                    )}
                {isDataAvailable &&
                    cardType === MyLearningCardType.NO_ASSIGNMENTS_ASSIGNED && (
                        <div
                            className={`${baseClassName}__content_no-assignments-assigned`}
                        >
                            <img
                                src={NoAssignmentsAssigned}
                                alt="No Assignments Assigned"
                            />
                            <div
                                className={`${baseClassName}__content_no-assignments-assigned-title`}
                            >
                                No assignments yet
                            </div>
                            <div
                                className={`${baseClassName}__content_no-assignments-assigned-descp`}
                            >
                                Your assignments will appear here.
                                <br />
                                Check back soon!
                            </div>
                        </div>
                    )}
            </Card>
        );
    };

    return (
        <div className={`${baseClassName}`}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {isUserLoggedIn ? (
                hasSucceeded(appRuntimeConfigsAsyncStatus) ? (
                    renderAssignmentDetails(type, assignment)
                ) : (
                    <Loader
                        isVisible
                        isBlocking={false}
                        className={`${baseClassName}-loader`}
                    />
                )
            ) : (
                <LearningCardLogoutView baseClassName={baseClassName} />
            )}
        </div>
    );
};

export default MyLearningCard;
